import { t } from "../../shared/i18n/index.js";

export const StorageHealthState = {
  HEALTHY: "healthy",
  DEGRADED: "degraded",
  BROKEN: "broken",
};

const PROBE_INTERVAL_MS = 30000;
const LOAD_TIMEOUT_MS = 30000;
const PROBE_KEY = "__orbital_storage_health_probe__";

let state = StorageHealthState.HEALTHY;
let reason = null;
const listeners = new Set();

export class StorageUnavailableError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = "StorageUnavailableError";
    this.reason = details.reason || message;
  }
}

export function getState() {
  return state;
}

export function isHealthy() {
  return state !== StorageHealthState.BROKEN;
}

export function getReason() {
  return reason;
}

export function assertHealthy() {
  if (isHealthy()) return;
  // Shown by the UI that attempted the write (toast, form error).
  throw new StorageUnavailableError(t("sync.storage.writesBlocked"), { reason });
}

// Signing out: connections close without waiting again for the server to
// confirm changes it may never confirm (offline). Just before, the sign-out
// tried a last sync and named every change still not on the server; the user
// chose to sign out anyway (engine/auth/handlers.js, logout).
let signingOut = false;

export function setSigningOut(value) {
  signingOut = !!value;
}

/** Whether a connection closing now may leave changes unconfirmed (see setSigningOut). */
export function isSigningOut() {
  return signingOut;
}

export function markBroken(nextReason = "storage_unavailable") {
  if (state === StorageHealthState.BROKEN) return false;
  state = StorageHealthState.BROKEN;
  reason = String(nextReason || "storage_unavailable");
  notify();
  return true;
}

export function markRecovered() {
  if (state === StorageHealthState.HEALTHY && reason === null) return false;
  state = StorageHealthState.HEALTHY;
  reason = null;
  notify();
  return true;
}

export function onChange(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

export function waitForIndexedDBSync(persistence, label, timeoutMs = LOAD_TIMEOUT_MS) {
  if (!persistence) {
    const err = new Error("IndexedDB persistence was not created");
    markBroken("indexeddb_load_failed");
    return Promise.reject(err);
  }

  if (persistence.synced) return Promise.resolve(persistence);

  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;

    const cleanup = () => {
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };

    const fail = (err) => {
      if (settled) return;
      cleanup();
      markBroken("indexeddb_load_failed");
      reject(err instanceof Error ? err : new Error(String(err)));
    };

    timeoutId = setTimeout(() => {
      fail(new Error(`IndexedDB load timed out for ${label}`));
    }, timeoutMs);

    persistence.once("synced", () => {
      if (settled) return;
      cleanup();
      resolve(persistence);
    });

    // y-indexeddb (as of 2.x) does not emit an `error` event and does not
    // attach its own `.catch` to the internal _db open promise. We
    // deliberately reach into _db here as a best-effort fast path for open
    // failures (disk full, IDB blocked by browser, etc.); the LOAD_TIMEOUT_MS
    // above is the safety net if this private property ever disappears.
    persistence.whenSynced?.catch?.(fail);
    if (persistence._db?.catch) {
      persistence._db.catch(fail);
    }
  });
}

export function monitorIndexedDBPersistence(
  persistence,
  label,
  { intervalMs = PROBE_INTERVAL_MS } = {},
) {
  let stopped = false;
  let intervalId = null;
  // A discarded persistence (its database is being deleted on purpose, see
  // DocumentPersistence.discard) is not a storage failure.
  const onError = error => { if (!stopped && !persistence?._discarded) markBroken(indexedDBReason(error)); };
  persistence?.on?.("error", onError);

  const runProbe = async () => {
    if (stopped || !persistence || persistence._discarded || state === StorageHealthState.BROKEN) return;
    try {
      await persistence.set(PROBE_KEY, {
        label,
        timestamp: Date.now(),
      });
    } catch (err) {
      if (stopped || persistence._discarded) return;
      markBroken(indexedDBReason(err));
    }
  };

  runProbe();
  intervalId = setInterval(runProbe, intervalMs);

  return () => {
    stopped = true;
    persistence?.off?.("error", onError);
    if (intervalId) clearInterval(intervalId);
  };
}

export function indexedDBReason(err) {
  const name = err?.name || "";
  const message = String(err?.message || err || "");
  if (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    /quota|disk.*full|no space/i.test(message)
  ) {
    return "indexeddb_quota";
  }
  return "indexeddb_unavailable";
}

export function isQuotaError(err) {
  return err?.name === "QuotaExceededError" ||
    err?.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    err?.code === 22 ||
    /quota|disk.*full|no space/i.test(String(err?.message || err || ""));
}

function notify() {
  for (const listener of listeners) {
    try {
      listener({ state, reason });
    } catch (err) {
      console.error("[StorageHealth] Listener failed:", err);
    }
  }
}

export function _resetForTests() {
  state = StorageHealthState.HEALTHY;
  reason = null;
  listeners.clear();
}
